import { ref, onBeforeMount } from "vue";
import router from "@/router/index";
import useApiHelpers from "@/composables/api/helpers";
import usePaymentEndpoints from "@/composables/api/usePaymentEndpoints";
import useModalActions from "@/composables/store/modal/useModalActions";

const { getRequest, postRequest } = useApiHelpers();
const { validateApplePaySessionEndpoint } = usePaymentEndpoints();
const { toggleActionModal } = useModalActions();

export default () => {
  let applepaySession;
  const isHidden = ref(true);
  const isDisabled = ref(false);
  const applePayEventEmits = ref({
    onPayAttempt: null,
    onPayFailure: null,
    onSessionValidationError: null,
  });

  const logApplePayMsg = (logMsg) => {
    postRequest("/logging/log", {
      data: logMsg,
    });
  };

  // const applePayIsAvailable = computed(
  //   () => window.ApplePaySession && ApplePaySession.canMakePayments()
  // );

  const handleApplePayEvents = (
    backendValidateSessionURL,
    successCallback,
    failureCallback
  ) => {
    try {
      applepaySession.begin();
    } catch (e) {
      logApplePayMsg(`ApplePaySession Exception: ${e.message}`);
    }

    applepaySession.onvalidatemerchant = (event) => {
      logApplePayMsg(
        `window applePayIsAvailable: ${
          window.ApplePaySession ? "true" : "false"
        } and canMakePayments: ${
          ApplePaySession.canMakePayments() ? "true" : "false"
        }`
      );

      const subdomain = window.location.hostname.split(".")[0];

      validateApplePaySessionEndpoint(
        backendValidateSessionURL,
        subdomain,
        event.validationURL,
        // success
        (merchantSession) => {
          logApplePayMsg(
            "success callback on validating session: ",
            merchantSession
          );
          applepaySession.completeMerchantValidation(merchantSession);
        },
        // failure
        (message) => {
          const modalOptions = {
            text: `Oops! There was an issue with your apple pay session. Please try again.`,
          };

          logApplePayMsg("failure callback on validating session: ", message);
          applepaySession.abort();
          // throw modal and redirect to order page with new a payment?
          toggleActionModal(true, modalOptions);
        }
      );
    };

    applepaySession.oncancel = (event) => {
      const modalOptions = {
        text: `Your apple payment has failed. Please try again.`,
      };
      applepaySession.abort();
      toggleActionModal(true, modalOptions);
    };

    applepaySession.onpaymentauthorized = (event) => {
      postRequest(
        "/prepaid/applepay",
        { token: event.payment.token, status: "success" },
        async () => {
          applepaySession.completePayment(applepaySession.STATUS_SUCCESS);
          const res = await getRequest("/orderId");
          successCallback(res.data.orderId);
        },
        () => {
          applepaySession.completePayment(applepaySession.STATUS_FAILURE);
          failureCallback();
        }
      );
    };
  };

  const startApplePaySession = (
    {
      backendValidateSessionURL,
      transactionInfo: { countryCode, currencyCode, orderInfo },
    },
    successCallback,
    failureCallback
  ) => {
    logApplePayMsg(
      `Apple props from backend:
                countryCode: ${countryCode},
                currencyCode: ${currencyCode}
                orderInfo: ${JSON.stringify(orderInfo)}`
    );

    applepaySession = new ApplePaySession(12, {
      currencyCode,
      countryCode,
      merchantCapabilities: ["supports3DS"],
      supportedNetworks: ["visa", "masterCard"],
      requiredBillingContactFields: ["postalAddress", "name", "phoneticName"],
      requiredShippingContactFields: [
        "postalAddress",
        "name",
        "phone",
        "email",
      ],
      total: orderInfo,
    });

    handleApplePayEvents(
      backendValidateSessionURL,
      successCallback,
      failureCallback
    );
  };

  onBeforeMount(() => {
    if (window.ApplePaySession && ApplePaySession.abort) {
      ApplePaySession.abort();
    }
  });

  const UIController = () => {
    const toggleButtonVisibility = (visibility) => {
      isHidden.value = visibility;
    };
    const toggleButtonDisable = (disabled) => {
      isDisabled.value = disabled;
    };

    return {
      toggleButtonVisibility,
      toggleButtonDisable,
    };
  };

  const isApplePayAvailable = () => !!window.ApplePaySession;

  return {
    UIController,
    applePayEventEmits,
    startApplePaySession,
    isApplePayAvailable,
  };
};
